// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  panelNumber,
} from "../core/display.js";
import {
  rawMetricDescriptor,
} from "../core/raw-metadata.js";
import {
  canonicalValueForField,
} from "../core/schema.js";
import {
  isRotaxEngineContext,
} from "../core/engine-family.js";
import {
  oilLimitDefaultsForMake,
} from "../core/engine-catalog.js";
import {
  clampScore,
  rankedQuantile,
} from "./utils.js";
import {
  oilSystemParameters,
} from "./detectors/oil-system.js";

function oilSampleIntervalSeconds(points) {
  const diffs = [];
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1].elapsedSeconds;
    const current = points[index].elapsedSeconds;
    if (Number.isFinite(previous) && Number.isFinite(current) && current > previous) {
      diffs.push(current - previous);
    }
  }
  return rankedQuantile(diffs, 0.5) ?? oilSystemParameters.defaultSampleSeconds;
}

function oilChannelSuffix(key) {
  const match = /^(?:oilPressure|oilTemp)([12])$/.exec(String(key ?? ""));
  return match?.[1] ?? "";
}

function oilContextValue(point, baseKey, suffix = "") {
  if (suffix) {
    const scoped = canonicalValueForField(point, `${baseKey}${suffix}`);
    if (Number.isFinite(scoped)) return scoped;
  }
  return canonicalValueForField(point, baseKey);
}

function oilSystemRunningPoint(point, key = "") {
  const phase = String(point?.phase ?? "");
  if (["Startup", "Shutdown"].includes(phase)) return false;
  const suffix = oilChannelSuffix(key);
  const rpm = oilContextValue(point, "rpm", suffix);
  if (Number.isFinite(rpm) && rpm <= oilSystemParameters.runningRpm) return false;
  if (oilSystemParameters.flightPowerPhases.includes(phase)) return true;
  if (oilSystemParameters.groundReviewPhases.includes(phase)) {
    const fuelFlow = oilContextValue(point, "fuelFlow", suffix);
    return Number.isFinite(fuelFlow) && fuelFlow >= oilSystemParameters.groundReviewFuelFlowGph;
  }
  const percentPower = oilContextValue(point, "percentPower", suffix);
  if (Number.isFinite(percentPower)) return percentPower >= oilSystemParameters.groundConcernPercentPower;
  const fuelFlow = oilContextValue(point, "fuelFlow", suffix);
  if (Number.isFinite(fuelFlow) && fuelFlow >= oilSystemParameters.groundConcernFuelFlowGph) return true;
  return Number.isFinite(rpm) && rpm >= oilSystemParameters.groundConcernRpm;
}

function oilMetricChannelKeys(data, metric, options = {}) {
  if (options.metricScoreScope === "engine") return [metric];
  const sideKeys = metric === "oilPressure"
    ? ["oilPressure1", "oilPressure2"]
    : ["oilTemp1", "oilTemp2"];
  const sideChannels = sideKeys.filter((key) => data.some((point) => Number.isFinite(canonicalValueForField(point, key))));
  return sideChannels.length ? sideChannels : [metric];
}

function oilConditionWindows(rows, key, predicate, label, contextForPoint = () => null) {
  const windows = [];
  const sampleSeconds = oilSampleIntervalSeconds(rows);
  let start = null;
  let end = null;
  let count = 0;
  let worstValue = null;
  let context = null;
  const flush = () => {
    if (start && end) windows.push({ start, end, count, worstValue, label, ...context,
      duration: Number.isFinite(start.elapsedSeconds) && Number.isFinite(end.elapsedSeconds)
        ? end.elapsedSeconds - start.elapsedSeconds + sampleSeconds : 0 });
    start = null;
    end = null;
    count = 0;
    worstValue = null;
  };
  rows.forEach((point) => {
    const nextContext = contextForPoint(point);
    const gap = end ? point.elapsedSeconds - end.elapsedSeconds : 0;
    if (end && (!(gap > 0) || gap > sampleSeconds * oilSystemParameters.maxGapSampleMultiple
      || JSON.stringify(context) !== JSON.stringify(nextContext))) flush();
    const value = canonicalValueForField(point, key);
    if (Number.isFinite(value) && predicate(value, point)) {
      if (!start) start = point;
      context = nextContext;
      end = point;
      count += 1;
      if (!Number.isFinite(worstValue)) {
        worstValue = value;
      } else if (/low/i.test(label)) {
        worstValue = Math.min(worstValue, value);
      } else {
        worstValue = Math.max(worstValue, value);
      }
      return;
    }
    flush();
  });
  flush();
  return windows;
}

function oilContextConditionWindows(data, key, predicate, label) {
  return oilConditionWindows(data, key, (value, point) => predicate(value, point), label)
    .map((window) => ({
      ...window,
      key,
      startTime: window.start?.elapsedSeconds,
      endTime: window.end?.elapsedSeconds,
      context: "low-rpm-or-low-power",
    }));
}

function oilLabelForKey(key, options = {}) {
  return options.labelForKey?.(key) ?? rawMetricDescriptor(key)[0];
}

function oilProfileMake(options = {}) {
  return options.aircraftProfile?.engine?.make
    ?? options.parserMetadata?.engineMake
    ?? null;
}

// The per-aircraft configured max (issue #200) wins; otherwise the make-based
// default (245 F, or 266 F for Rotax whose oil runs to 130 C). Rotax family is
// honored even when only the family (not the make string) is known, so a loose
// profile never mistakenly flags normal Rotax oil temperature.
function resolvedOilMaxTempF(options = {}) {
  const stored = options.aircraftProfile?.operatingLimits?.oilTempMax;
  if (Number.isFinite(stored) && stored > 0) return stored;
  const fromMake = oilLimitDefaultsForMake(oilProfileMake(options)).oilTempMaxF;
  const rotaxMax = oilSystemParameters.temperature.rotax.reviewHighF;
  return fromMake < rotaxMax && isRotaxEngineContext(options) ? rotaxMax : fromMake;
}

function resolvedOilMaxPressurePsi(options = {}) {
  const stored = options.aircraftProfile?.operatingLimits?.oilPressureMax;
  if (Number.isFinite(stored) && stored > 0) return stored;
  const fromMake = oilLimitDefaultsForMake(oilProfileMake(options)).oilPressureMaxPsi;
  // Rotax oil pressure runs higher; honor its default even when only the family
  // (not the make string) is known, matching the temperature fallback above.
  return fromMake === oilLimitDefaultsForMake(null).oilPressureMaxPsi && isRotaxEngineContext(options)
    ? oilLimitDefaultsForMake("Rotax").oilPressureMaxPsi
    : fromMake;
}

// The configured/default max sets the review (attention) line; the earlier
// watch line stays put unless the max sits below it. Only the high side is
// configurable (issue #200) — low-pressure and warm-up thresholds are unchanged.
function oilTemperatureThresholds(options = {}) {
  const base = oilSystemParameters.temperature;
  const merged = isRotaxEngineContext(options)
    ? { ...base, watchHighF: base.rotax.watchHighF, reviewHighF: base.rotax.reviewHighF, highWatch: base.rotax.highWatch }
    : base;
  const reviewHighF = resolvedOilMaxTempF(options);
  const watchHighF = Math.min(merged.watchHighF, reviewHighF);
  return { ...merged, reviewHighF, watchHighF };
}

function oilPressureThresholds(options = {}) {
  const base = oilSystemParameters.pressure;
  const reviewHighPsi = resolvedOilMaxPressurePsi(options);
  // Pressure has no separate high-watch band (unlike temperature): keep watch ==
  // review so no phantom "high oil pressure watch" window appears above 100 psi.
  return { ...base, reviewHighPsi, watchHighPsi: reviewHighPsi };
}

function positiveOilLimit(value) {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function oilOptionsForChannel(key, options) {
  const scopedEngine = options.metricScoreScope === "engine" ? options.analysisEngine : null;
  const index = scopedEngine ? scopedEngine.engineNumber - 1 : Number(oilChannelSuffix(key)) - 1;
  const profile = options.aircraftProfile ?? {};
  const shared = profile.operatingLimits?.oilPressure ?? {};
  const engine = scopedEngine ?? options.engineDescriptors?.[index];
  const override = [engine?.id, engine?.engineKey, profile.engineLayout?.engineIds?.[index], `engine${index + 1}`, String(index + 1)]
    .filter(Boolean).map((id) => shared.byEngine?.[id]).find(Boolean);
  if (index < 0 || !override) return options;
  return { ...options, aircraftProfile: { ...profile, operatingLimits: { ...profile.operatingLimits,
    ...(positiveOilLimit(override.maxPsi) ? { oilPressureMax: override.maxPsi } : {}),
    oilPressure: { ...shared, ...override } } } };
}

function oilPressureLimits(options) {
  const limits = options.aircraftProfile?.operatingLimits ?? {};
  const settings = limits.oilPressure ?? {};
  const maxPsi = positiveOilLimit(limits.oilPressureMax);
  const validMin = (value) => positiveOilLimit(value) && (!maxPsi || value < maxPsi) ? value : null;
  return { operatingMinPsi: validMin(settings.operatingMinPsi), idleMinPsi: validMin(settings.idleMinPsi),
    idleMaxRpm: positiveOilLimit(settings.idleMaxRpm) ?? oilSystemParameters.idleMaxRpm,
    maxPsi, coldMaxPsi: positiveOilLimit(settings.coldMaxPsi), coldMaxTempF: positiveOilLimit(settings.coldMaxTempF),
    coldMaxRpm: positiveOilLimit(settings.coldMaxRpm),
    source: String(settings.source ?? "").trim() || null,
    measurementLocation: String(settings.measurementLocation ?? "").trim() || null };
}

function oilPressurePointContext(point, key, options) {
  const suffix = oilChannelSuffix(key);
  const limits = options.pressureLimits;
  const scopedValue = (base) => canonicalValueForField(point, `${base}${suffix}`);
  const rpm = scopedValue("rpm");
  const phase = String(point.phase ?? "");
  if ((Number.isFinite(rpm) && rpm <= oilSystemParameters.runningRpm) || phase === "Shutdown") return null;
  const power = scopedValue("percentPower");
  const fuelFlow = scopedValue("fuelFlow");
  const idle = Number.isFinite(rpm) ? rpm <= limits.idleMaxRpm
    : Number.isFinite(power) && power >= 0 && power < oilSystemParameters.groundConcernPercentPower;
  const loaded = Number.isFinite(rpm) && rpm > limits.idleMaxRpm
    || Number.isFinite(power) && power >= oilSystemParameters.groundConcernPercentPower
    || Number.isFinite(fuelFlow) && fuelFlow >= oilSystemParameters.groundConcernFuelFlowGph
    || !suffix && oilSystemParameters.flightPowerPhases.includes(phase);
  const scopedPowerKnown = idle || loaded;
  const temp = scopedValue("oilTemp");
  const cold = limits.coldMaxPsi && limits.coldMaxTempF && limits.coldMaxRpm
    && limits.maxPsi && limits.coldMaxPsi >= limits.maxPsi
    && Number.isFinite(rpm) && rpm <= limits.coldMaxRpm
    && Number.isFinite(temp) && temp <= limits.coldMaxTempF
    && Number.isFinite(point.elapsedSeconds) && point.elapsedSeconds >= 0
    && point.elapsedSeconds <= oilSystemParameters.coldStartMaxSeconds
    && ["Startup", "Taxi", "Runup", "Ground Run"].includes(phase)
    && !(Number.isFinite(point.groundSpeed) && point.groundSpeed > 30)
    && point.onGround !== false && point.weightOnWheels !== false;
  const buildingPressure = phase === "Startup" && Number.isFinite(options.firstRunningTime)
    && Number.isFinite(point.elapsedSeconds)
    && point.elapsedSeconds - options.firstRunningTime < oilSystemParameters.startupPressureGraceSeconds;
  if (phase === "Startup" && !cold && (!limits.idleMinPsi || !Number.isFinite(rpm))) return null;
  if (!idle && !oilSystemRunningPoint(point, key) && !cold) return null;
  if (idle && !limits.idleMinPsi && !cold && !oilSystemRunningPoint(point, key)) return null;
  const minimum = !scopedPowerKnown ? null : idle ? limits.idleMinPsi : limits.operatingMinPsi;
  const base = oilPressureThresholds(options);
  return { operatingState: !scopedPowerKnown ? "unknown" : cold ? "cold-start" : idle ? "idle" : "loaded",
    reviewLow: buildingPressure ? -Infinity : minimum ?? base.reviewLowPsi,
    watchLow: buildingPressure ? -Infinity : minimum ?? base.watchLowPsi,
    reviewHigh: cold ? limits.coldMaxPsi : base.reviewHighPsi,
    limitsKnown: Boolean(minimum && limits.maxPsi),
    limitSource: limits.source ?? (minimum ? "configured profile" : "generic screen"),
    measurementLocation: limits.measurementLocation };
}

function oilSummaryContextLabel(summary) {
  const label = String(summary?.label ?? "").trim();
  if (!label) return "";
  if (/^(?:oilPressure|oilTemp)(?:1|2|Left|Right)$/i.test(String(summary?.key ?? ""))) return label;
  const generic = String(rawMetricDescriptor(summary.key)[0] ?? "").trim();
  return label && label !== generic ? label : "";
}

function oilSummaryIssueNote(summary, issue = oilSummaryIssue(summary)) {
  if (!issue) return "";
  const context = oilSummaryContextLabel(summary);
  return context
    ? `${context}: ${issue.label.toLowerCase()} ${issue.detail}`
    : `${issue.label}: ${issue.detail}`;
}

function oilChannelSummary(data, key, kind, options = {}) {
  const pressure = kind === "pressure";
  const pressureLimits = pressure ? oilPressureLimits(options) : null;
  const firstRunningTime = data.find((point) => {
    const rpm = canonicalValueForField(point, `rpm${oilChannelSuffix(key)}`);
    return Number.isFinite(rpm) && rpm > oilSystemParameters.runningRpm;
  })?.elapsedSeconds;
  const contexts = new Map(data.map((point) => [point, pressure
    ? oilPressurePointContext(point, key, { ...options, pressureLimits, firstRunningTime })
    : oilSystemRunningPoint(point, key) ? { operatingState: "loaded" } : null]));
  const rows = data.filter((point) => contexts.get(point));
  if (rows.length < oilSystemParameters.minRunningRows) return null;
  const sampleTimes = new Set(rows.filter((point) => Number.isFinite(canonicalValueForField(point, key)))
    .map((point) => point.elapsedSeconds).filter(Number.isFinite));
  if (sampleTimes.size < oilSystemParameters.minRunningRows) return null;
  const values = rows.map((point) => canonicalValueForField(point, key)).filter(Number.isFinite);
  if (!values.length) return null;
  const extent = extentFromValues(values);
  const thresholds = pressure ? oilPressureThresholds(options) : oilTemperatureThresholds(options);
  const reviewLow = pressure ? thresholds.reviewLowPsi : thresholds.reviewLowF;
  const watchLow = pressure ? thresholds.watchLowPsi : thresholds.watchLowF;
  const watchHigh = pressure ? thresholds.watchHighPsi : thresholds.watchHighF;
  const reviewHigh = pressure ? thresholds.reviewHighPsi : thresholds.reviewHighF;
  const thresholdFor = (point, level) => pressure ? contexts.get(point)?.[level]
    : ({ reviewLow, watchLow, reviewHigh, watchHigh })[level];
  const lowReviewTest = (value, point) => value < thresholdFor(point, "reviewLow");
  const lowWatchTest = (value, point) => value < thresholdFor(point, "watchLow");
  const highReviewTest = (value, point) => value > thresholdFor(point, "reviewHigh");
  const highWatchTest = (value, point) => value > thresholdFor(point, pressure ? "reviewHigh" : "watchHigh");
  const matchingValues = (predicate) => rows.filter((point) => Number.isFinite(canonicalValueForField(point, key))
    && predicate(canonicalValueForField(point, key), point)).map((point) => canonicalValueForField(point, key));
  const lowReviewValues = matchingValues(lowReviewTest);
  const lowWatchValues = matchingValues(lowWatchTest);
  const highWatchValues = matchingValues(highWatchTest);
  const highReviewValues = matchingValues(highReviewTest);
  const conditionWindows = (predicate, label, level) => oilConditionWindows(data, key,
    (value, point) => Boolean(contexts.get(point)) && predicate(value, point), label, (point) => {
      const context = contexts.get(point);
      return context ? { operatingState: context.operatingState, threshold: thresholdFor(point, level),
        ...(pressure ? { limitSource: context.limitSource, measurementLocation: context.measurementLocation } : {}) } : null;
    });
  const windows = [
    ...conditionWindows(lowReviewTest, pressure ? "Low oil pressure" : "Cold oil temperature", "reviewLow"),
    ...conditionWindows((value, point) => !lowReviewTest(value, point) && lowWatchTest(value, point), pressure ? "Low oil pressure watch" : "Cold oil temperature watch", "watchLow"),
    ...conditionWindows((value, point) => highWatchTest(value, point) && !highReviewTest(value, point), pressure ? "High oil pressure watch" : "High oil temperature watch", pressure ? "reviewHigh" : "watchHigh"),
    ...conditionWindows(highReviewTest, pressure ? "High oil pressure" : "High oil temperature", "reviewHigh"),
  ].map((window) => ({
    ...window,
    key,
    startTime: window.start?.elapsedSeconds,
    endTime: window.end?.elapsedSeconds,
  }));
  const longEnough = (label) => windows.some((window) => window.label === label
    && window.duration >= oilSystemParameters.sustainedWindow.minSeconds);
  const lowReviewRatio = lowReviewValues.length / values.length;
  const lowWatchRatio = lowWatchValues.length / values.length;
  const highWatchRatio = highWatchValues.length / values.length;
  const highReviewRatio = highReviewValues.length / values.length;
  const sustainedLowReview = longEnough(pressure ? "Low oil pressure" : "Cold oil temperature");
  const sustainedLowWatch = sustainedLowReview || longEnough(pressure ? "Low oil pressure watch" : "Cold oil temperature watch");
  const sustainedHighReview = longEnough(pressure ? "High oil pressure" : "High oil temperature");
  const sustainedHighWatch = sustainedHighReview || longEnough(pressure ? "High oil pressure watch" : "High oil temperature watch");
  const sustainedExtent = (label) => extentFromValues(windows.filter((window) => window.label === label
    && window.duration >= oilSystemParameters.sustainedWindow.minSeconds).map((window) => window.worstValue));
  let penalty = 0;
  if (pressure && lowReviewValues.length && sustainedLowReview) {
    const config = thresholds.lowReview;
    penalty = Math.max(penalty, config.basePenalty + Math.min(config.ratioPenaltyCap, lowReviewRatio * config.ratioWeight));
    const excursionMin = sustainedExtent("Low oil pressure")?.min;
    if (excursionMin < thresholds.severeLowPsi) penalty = Math.max(penalty, config.severePenalty);
    else if (excursionMin < thresholds.strongLowPsi) penalty = Math.max(penalty, config.strongPenalty);
  }
  if (pressure && lowWatchValues.length && sustainedLowWatch) {
    const config = thresholds.lowWatch;
    penalty = Math.max(penalty, config.basePenalty + Math.min(config.ratioPenaltyCap, lowWatchRatio * config.ratioWeight));
  }
  if (pressure && highReviewValues.length && sustainedHighReview) {
    const config = thresholds.highReview;
    penalty = Math.max(penalty, config.basePenalty + Math.min(config.ratioPenaltyCap, highReviewRatio * config.ratioWeight));
  }
  if (!pressure && highReviewValues.length && sustainedHighReview) {
    const config = thresholds.highReview;
    penalty = Math.max(penalty, config.basePenalty + Math.min(config.ratioPenaltyCap, highReviewRatio * config.ratioWeight));
  }
  if (!pressure && highWatchValues.length && sustainedHighWatch) {
    const config = thresholds.highWatch;
    penalty = Math.max(penalty, config.basePenalty + Math.min(config.ratioPenaltyCap, highWatchRatio * config.ratioWeight));
  }
  if (!pressure && lowReviewValues.length && sustainedLowReview) {
    const config = thresholds.lowReview;
    penalty = Math.max(penalty, config.basePenalty + Math.min(config.ratioPenaltyCap, lowReviewRatio * config.ratioWeight));
  }
  let operatingTempReachedF = null;
  let runSeconds = null;
  let belowOperating = false;
  let belowOperatingDeficitF = null;
  if (!pressure && options.loaded) {
    const warmup = thresholds.warmup;
    runSeconds = oilConditionWindows(data, key, (_, point) => Boolean(contexts.get(point)), "Loaded oil temperature")
      .reduce((total, window) => total + window.duration, 0);
    operatingTempReachedF = rankedQuantile(values, warmup.operatingQuantile);
    if (Number.isFinite(operatingTempReachedF)
      && runSeconds >= warmup.minRunSeconds
      && operatingTempReachedF < warmup.watchF) {
      belowOperating = true;
      belowOperatingDeficitF = warmup.watchF - operatingTempReachedF;
      const warmupPenalty = Math.min(
        warmup.maxPenalty,
        warmup.basePenalty + belowOperatingDeficitF * warmup.perDegreePenalty,
      );
      penalty = Math.max(penalty, warmupPenalty);
    }
  }
  const score = clampScore(100 - penalty);
  const issueLabel = pressure && sustainedLowReview ? "Low oil pressure"
    : pressure && sustainedLowWatch ? "Low oil pressure watch"
      : sustainedHighReview ? pressure ? "High oil pressure" : "High oil temperature"
        : sustainedHighWatch ? "High oil temperature watch" : null;
  const topWindow = windows
    .filter((window) => !issueLabel || window.label === issueLabel
      && (!pressure || window.duration >= oilSystemParameters.sustainedWindow.minSeconds))
    .slice()
    .sort((a, b) => (pressure && issueLabel
      ? /Low/.test(issueLabel) ? a.worstValue - b.worstValue : b.worstValue - a.worstValue : 0)
      || (b.duration ?? 0) - (a.duration ?? 0))[0] ?? null;
  return {
    key,
    label: oilLabelForKey(key, options),
    kind,
    score,
    penalty,
    count: values.length,
    min: extent?.min ?? null,
    max: extent?.max ?? null,
    average: averageValues(values),
    ...(pressure ? { pressureLimits, limitsKnown: rows.filter((point) => Number.isFinite(canonicalValueForField(point, key)))
      .every((point) => contexts.get(point).limitsKnown),
      lowReviewMin: sustainedExtent("Low oil pressure")?.min ?? null,
      lowWatchMin: sustainedExtent("Low oil pressure watch")?.min ?? null,
      highReviewMax: sustainedExtent("High oil pressure")?.max ?? null } : {}),
    cumulativeExposureSeconds: windows.reduce((total, window) => total + window.duration, 0),
    longestExcursionSeconds: Math.max(0, ...windows.map((window) => window.duration)),
    lowReviewRatio,
    lowWatchRatio,
    highWatchRatio,
    highReviewRatio,
    lowReviewCount: lowReviewValues.length,
    lowWatchCount: lowWatchValues.length,
    highWatchCount: highWatchValues.length,
    highReviewCount: highReviewValues.length,
    sustainedLowReview,
    sustainedLowWatch,
    sustainedHighWatch,
    sustainedHighReview,
    operatingTempReachedF,
    runSeconds,
    belowOperating,
    belowOperatingDeficitF,
    windows,
    topWindow,
  };
}

export function oilSummaryIssue(summary) {
  if (!summary) return null;
  const pressure = summary.kind === "pressure";
  const unit = pressure ? " psi" : " F";
  if (pressure && summary.lowReviewCount && summary.sustainedLowReview) {
    const value = summary.lowReviewMin ?? summary.min;
    return {
      severity: 3,
      label: "Low oil pressure",
      detail: `min ${panelNumber(value, " psi")}`,
      value,
      unit,
    };
  }
  if (pressure && summary.lowWatchCount && summary.sustainedLowWatch) {
    const value = summary.lowWatchMin ?? summary.min;
    return {
      severity: 2,
      label: "Oil pressure watch",
      detail: `min ${panelNumber(value, " psi")}`,
      value,
      unit,
    };
  }
  if (pressure && summary.highReviewCount && summary.sustainedHighReview) {
    const value = summary.highReviewMax ?? summary.max;
    return {
      severity: 2,
      label: "High oil pressure",
      detail: `max ${panelNumber(value, " psi")}`,
      value,
      unit,
    };
  }
  if (!pressure && summary.highReviewCount && summary.sustainedHighReview) {
    return {
      severity: 3,
      label: "High oil temperature",
      detail: `max ${panelNumber(summary.max, " F")}`,
      value: summary.max,
      unit,
    };
  }
  if (!pressure && summary.highWatchCount && summary.sustainedHighWatch) {
    return {
      severity: 2,
      label: "Oil temperature watch",
      detail: `max ${panelNumber(summary.max, " F")}`,
      value: summary.max,
      unit,
    };
  }
  if (!pressure && summary.belowOperating) {
    const target = oilSystemParameters.temperature.warmup.targetF;
    return {
      severity: 2,
      label: "Oil below operating temperature",
      detail: `upper-range oil temperature ${panelNumber(summary.operatingTempReachedF, " F")} during loaded operation (target ${target} F). Compare the temperature trend on similar flights; the log does not establish a cause.`,
      value: summary.operatingTempReachedF,
      unit,
    };
  }
  if (!pressure && summary.lowReviewCount) {
    return {
      severity: 1,
      label: "Cold oil temperature",
      detail: `min ${panelNumber(summary.min, " F")}`,
      value: summary.min,
      unit,
    };
  }
  return null;
}

export function analyzeOilSystem(data, options = {}) {
  const pressureKeys = oilMetricChannelKeys(data, "oilPressure", options);
  const tempKeys = oilMetricChannelKeys(data, "oilTemp", options);
  const hasOilSamples = [...pressureKeys, ...tempKeys].some((key) =>
    data.some((point) => Number.isFinite(canonicalValueForField(point, key))));
  const pressureContextWindows = pressureKeys.flatMap((key) => oilContextConditionWindows(
    data,
    key,
    (value, point) => {
      const channelOptions = oilOptionsForChannel(key, options);
      return value < oilSystemParameters.pressure.watchLowPsi
        && !oilPressurePointContext(point, key, { ...channelOptions, pressureLimits: oilPressureLimits(channelOptions) });
    },
    "Low oil pressure low-RPM context",
  ).map((window) => ({
    ...window,
    channelLabel: oilLabelForKey(key, options),
    kind: "pressure",
  })));
  const summaries = [
    ...pressureKeys.map((key) => oilChannelSummary(data, key, "pressure", oilOptionsForChannel(key, options))),
    ...tempKeys.map((key) => {
      const rows = data.filter((point) => oilSystemRunningPoint(point, key));
      const loaded = rows.length >= oilSystemParameters.minRunningRows;
      if (!loaded) return null;
      return oilChannelSummary(data, key, "temperature", { ...oilOptionsForChannel(key, options), loaded });
    }),
  ].filter(Boolean);
  if (!summaries.length) {
    return {
      key: "oilSystem",
      score: null,
      note: hasOilSamples ? "No oil data during running engine" : "No oil temperature or pressure channels",
      details: {
        rowsChecked: 0,
        summaries: [],
        contextWindows: pressureContextWindows
          .filter((window) => Number.isFinite(window.startTime))
          .sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))
          .slice(0, 8),
      },
    };
  }
  const top = summaries
    .slice()
    .sort((a, b) => a.score - b.score || (b.penalty ?? 0) - (a.penalty ?? 0))[0];
  const issue = oilSummaryIssue(top);
  const score = Math.min(...summaries.map((summary) => summary.score));
  const pressureSummaries = summaries.filter((summary) => summary.kind === "pressure");
  const pressureLimitsKnown = pressureSummaries.length > 0 && pressureSummaries.every((summary) => summary.limitsKnown);
  const note = issue
    ? oilSummaryIssueNote(top, issue)
    : pressureLimitsKnown ? "No sustained oil excursion against configured limits"
      : "No sustained oil excursion; pressure limits incomplete";
  return {
    key: "oilSystem",
    score,
    note,
    details: {
      rowsChecked: Math.max(...summaries.map((summary) => summary.count), 0),
      summaries,
      pressureLimitsKnown,
      qualification: pressureLimitsKnown ? null : "Oil pressure uses a generic screen where operating-state limits are unknown. Confirm the applicable profile limits and measurement location.",
      top: {
        ...top,
        issue,
      },
      contextWindows: pressureContextWindows
        .filter((window) => Number.isFinite(window.startTime))
        .sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))
        .slice(0, 8),
      windows: summaries
        .flatMap((summary) => summary.windows.map((window) => ({
          ...window,
          channelLabel: summary.label,
          kind: summary.kind,
        })))
        .filter((window) => Number.isFinite(window.startTime)),
    },
  };
}
